import { ModalV2, TextInput } from "@/components";
import MultiCheckbox from "@/components/checkbox/multiple";
import { useGetTenantAllowedPagesQuery, useSetUserAllowedPagesMutation } from "@/graphql/page-access";
import { Typography } from "@mui/material";
import { userRightOptions } from "@/constants";
import { Box, Grid } from "@mui/material";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { StyledSelectBox, StyledSelectBoxContainer } from "./styles";
import { ValidationPatterns } from "@/utils/form-validation";
import InputErrorMessage from "@/components/input-error-message";
import { useSnackbar } from "@/hooks";

interface DataModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  buttonAction: (data: IUser) => void;
  buttonText: string;
  isEdit: IUser | null;
  buttonLoader: boolean;
}

const AddEditUserModal: React.FC<DataModalProps> = ({
  setShow,
  show,
  title,
  buttonText,
  buttonAction,
  isEdit,
  buttonLoader,
}) => {
  const { show: showSnackbar } = useSnackbar();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IUser>({
    defaultValues: {
      userRight: isEdit ? isEdit.accessLevel : userRightOptions[0].value,
      firstName: isEdit ? isEdit?.firstName : "",
      lastName: isEdit ? isEdit?.lastName : "",
      email: isEdit ? isEdit?.email : "",
    },
  });

  const [allowedPages, setAllowedPages] = React.useState<string[]>(
    isEdit?.allowedPages || []
  );
  const { data: tenantPagesResp } = useGetTenantAllowedPagesQuery();
  const [setUserAllowedPages] = useSetUserAllowedPagesMutation();
  const [savingPages, setSavingPages] = React.useState(false);

  const PAGE_OPTIONS: Array<{ key: string; label: string }> = [
    { key: "ANALYTICS", label: "Analytics" },
    { key: "POTENTIAL_SAVINGS", label: "Potential Savings" },
    { key: "SCENARIO_MODELING", label: "Scenario Modeling" },
    { key: "CHAT_ASSISTANCE", label: "Chat Assistance" },
    { key: "PRODUCT_HUB", label: "Product Hub" },
    { key: "TRADE_DATA", label: "Trade Data" },
    { key: "DOCUMENTS", label: "Documents" },
    { key: "POST_IMPORT_ADJUSTMENT", label: "Post Import Adjustment" },
  ];
  const availablePageKeys = React.useMemo(
    () => tenantPagesResp?.pages || [],
    [tenantPagesResp]
  );
  const filteredOptions = React.useMemo(
    () => PAGE_OPTIONS.filter((o) => availablePageKeys.includes(o.key)),
    [availablePageKeys]
  );
  const isSelectionDisabled = filteredOptions.length === 0;

  const onSubmit = async (data: IUser) => {
    const finalPages = allowedPages.filter((k) => availablePageKeys.includes(k));
    // For edit flow, save allowed pages first. If it fails, inform the user and keep modal open.
    if (isEdit?._id) {
      try {
        setSavingPages(true);
        await setUserAllowedPages({ userId: isEdit._id, pages: finalPages }).unwrap();
      } catch (err: any) {
        const description =
          err?.data?.message || err?.error || err?.message || "Please try again or contact support.";
        showSnackbar("Failed to update page access", description, "error", 6000);
        setSavingPages(false);
        return;
      }
      setSavingPages(false);
    }
    // Proceed with the primary create/update action
    await buttonAction({ ...data, allowedPages: finalPages });
  };

  const formFields: Array<{
    name: keyof IUser;
    label: string;
    type: string;
    rules: object;
    placeholder: string;
    disabled?: boolean;
  }> = [
    {
      name: "firstName",
      label: "First Name",
      type: "text",
      rules: { required: "First name is required" },
      placeholder: "eg John",
    },
    {
      name: "lastName",
      label: "Last Name",
      type: "text",
      rules: { required: "Last name is required" },
      placeholder: "eg Snow",
    },
    {
      name: "email",
      label: "Email Address*",
      type: "email",
      placeholder: "hi@example.com",
      disabled: !!isEdit,
      rules: {
        required: "Email is required",
        pattern: {
          value: ValidationPatterns.email,
          message: "Invalid email address",
        },
      },
    },
  ];

  return (
    <ModalV2
      setShow={setShow}
      show={show}
      buttonLoader={buttonLoader}
      isConfirmButtonLoading={buttonLoader || savingPages}
      title={title}
      buttonText={buttonText}
      buttonAction={handleSubmit(onSubmit)}
      modalStyles={{
        "& .MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded": {
          width: "500px",
        },
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <StyledSelectBoxContainer>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="userRight"
                control={control}
                rules={{ required: "Role is required" }}
                render={({ field }) => {
                  return (
                    <StyledSelectBox
                      disabled={false}
                      value={field.value as string}
                      options={userRightOptions}
                      label="Role"
                      handleChangeValue={field.onChange}
                    />
                  );
                }}
              />
              <InputErrorMessage message={errors.userRight?.message} />
            </Grid>

            {formFields.map((field) => (
              <Grid
                item
                xs={12}
                sm={field.name === "email" ? 12 : 6}
                key={field.name}
              >
                <Controller
                  name={field.name}
                  control={control}
                  rules={field.rules}
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      disabled={field.disabled}
                      placeholder={field.placeholder}
                      label={field.label}
                      type={field.type}
                      value={value as string}
                      handleChangeValue={onChange}
                      error={!!errors[field.name as keyof typeof errors]}
                    />
                  )}
                />
                <InputErrorMessage
                  message={errors[field.name as keyof typeof errors]?.message}
                />
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12}>
            <MultiCheckbox
              label="Pages"
              checkboxes={filteredOptions.map((o) => ({ label: o.label }))}
              checked={filteredOptions.map((o) => allowedPages.includes(o.key))}
              handleParentChange={(e)=>{
                const checked = e.target.checked;
                if (checked) setAllowedPages(filteredOptions.map((o) => o.key));
                else setAllowedPages([]);
              }}
              handleChildChange={(index)=>()=>{
                const key = filteredOptions[index]?.key;
                if (!key) return;
                setAllowedPages(prev => prev.includes(key) ? prev.filter(k=>k!==key) : [...prev, key]);
              }}
            />
            {isSelectionDisabled && (
              <Typography sx={{ color: "#8E9494", fontSize: "12px", mt: 0.5 }}>
                No tenant pages configured. Contact your administrator.
              </Typography>
            )}
          </Grid>
        </StyledSelectBoxContainer>
      </Box>
    </ModalV2>
  );
};

export default AddEditUserModal;
